import * as Sentry from '@sentry/react';
import config from '../config/config';
import type CallbackFunction from '../Interface/CallbackFunction';
import { type DocumentType } from '../Interface/Dashboard/Document';
import AuthService from './AuthService';
import { checkLimitFileSize } from './Calcul';

const headers = (token?: string): any => {
  const switchUser = AuthService.getSwitchUser();
  let requestHeaders: Record<string, string> = {
    Authorization: `Bearer ${token ? token : AuthService.getAccessToken()}`,
    'Content-Type': 'multipart/form-data',
  };
  if (switchUser) {
    requestHeaders = {
      Authorization: `Bearer ${token ? token : AuthService.getAccessToken()}`,
      'Content-Type': 'multipart/form-data',
      'x-switch-user': switchUser,
    };
  }
  return requestHeaders;
};

interface UploadFileParams {
  file: File;
  name: string;
  type: DocumentType;
  status: string;
  isOrganizationDoc: boolean;
  organizationId: number;
  userId?: number;
  lrID?: number;
  personId?: string;
}

export const UploadFileToApiDigidom = (params: UploadFileParams): Promise<any> => {
  const { file, name, status, type, lrID, organizationId, personId, userId, isOrganizationDoc } =
    params;

  if (localStorage.getItem('MOCK_UPLOAD') === 'true') {
    console.log('MOCK UPLOAD ACTIVE: Mocking file upload to API.', params);
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          id: Math.floor(Math.random() * 1000000),
          name: file.name,
          type: type,
          status: status || 'TO_APPROVE',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }, 1000);
    });
  }

  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    if (!checkLimitFileSize(file)) {
      reject({ message: 'Le ficher doit être inférieur à 5 Mo', type: 'ERROR' });
    } else if (
      file.type !== 'image/jpg' &&
      file.type !== 'image/jpeg' &&
      file.type !== 'image/png' &&
      file.type !== 'application/pdf'
    ) {
      reject({ message: "Le ficher n'est pas prise en charge", type: 'ERROR' });
    } else {
      const formData = new FormData();

      formData.append('documentFile', file, file.name);
      if (isOrganizationDoc) {
        const organization = `/v1/organizations/${organizationId}`;
        formData.append('organization', organization);
      }
      if (userId) {
        const user = `/v1/users/${userId}`;
        formData.append('user', user);
      }

      if (lrID) {
        const legalRepresentative = `/v1/legal_representatives/${lrID}`;
        formData.append('legalRepresentative', legalRepresentative);
      }

      if (personId) {
        if (isNaN(+personId)) {
          formData.append('person', personId);
        } else {
          formData.append('person', `/v1/people/${personId}`);
        }
      }

      formData.append('type', type);
      formData.append('status', status);
      formData.append('name', name);
      formData.append('organizationId', organizationId.toString());

      const options: RequestInit = {
        method: 'POST',
        headers: headers(),
        body: formData,
      };

      delete (options.headers as any)['Content-Type'];

      fetch(config.apiSymphonyDigidom + '/documents', options)
        .then((response) => response.json().then((document) => resolve(document)))
        .catch(() => reject({ message: "Impossible d'uploader le fichier", type: 'ERROR' }));
    }
  });
};

export const downloadFile = (link: string, name: string, cb: () => void) => {
  fetch(`${config.corsDigidom}?url=${link}`, { method: 'GET', headers: headers() })
    .then((response) => response.blob())
    .then((blob) => {
      const a = document.createElement('a');
      a.download = `${name}`;
      a.href = window.URL.createObjectURL(blob);
      a.click();
      alert('Téléchargement terminé');
    })
    .catch(() => {
      // Handle download error appropriately
      alert('Erreur lors du téléchargement');
    })
    .then(() => cb());
};

export const downloadLetters = (
  lettersIds: number[],
  organization: number,
  setLoading: CallbackFunction<boolean>,
) => {
  let link = `${config.apiSymphonyDigidom}/organizations/${organization}/download/letters`;

  if (lettersIds.length) {
    link = link.concat(`?letterIds[]=${lettersIds.join(',')}`);
  }

  fetch(`${link}`, {
    method: 'GET',
    headers: { ...headers(), 'Content-Type': 'application/zip' },
  })
    .then((response) => response.blob())
    .then((blob) => {
      const a = document.createElement('a');
      a.download = `letters_${new Date().toISOString()}.zip`;
      a.href = window.URL.createObjectURL(blob);
      a.click();
      alert('Téléchargement terminé');
    })
    .catch(() => {
      // Handle download error appropriately
      alert('Erreur lors du téléchargement');
    })
    .then(() => setLoading(false));
};

export const segmentTrack = (input: { email?: string; data: any; token?: string }) => {
  const { email, data, token } = input;
  let url = `${config.apiSymphonyDigidom}/segment/track`;
  if (email) {
    url = `${config.apiSymphonyDigidom}/segment/track?email=${email}`;
  }

  const options: RequestInit = {
    method: 'POST',
    headers: { ...headers(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  };

  return fetch(url, options)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status} ${response.statusText}`);
      }
      return response.json();
    })
    .catch((error) => {
      Sentry.captureException(error, {
        extra: {
          request: {
            url,
            method: options.method,
            headers: options.headers,
            body: options.body,
          },
          data,
          error,
        },
      });

      return Promise.reject(error);
    });
};

export const segmentIdentifyScanEnveloppe = (input: { email?: string; organizationId: string }) => {
  let url = `${config.apiSymphonyDigidom}/segment/identify-organization/${input.organizationId}`;
  if (input.email) {
    url = `${config.apiSymphonyDigidom}/segment/identify-organization/${input.organizationId}?email=${input.email}`;
  }

  return fetch(url, {
    method: 'POST',
    headers: { ...headers(), 'Content-Type': 'application/json' },
  });
};

export const segmentIdentify = (data: any) => {
  const url = `${config.apiSymphonyDigidom}/segment/identify`;

  return fetch(url, {
    method: 'POST',
    headers: { ...headers(), 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
};
